import FetchService from "../FetchService/CustomService";
import { downloadBlobAsFile, getFileName } from "../utilsv2";
import { toTitleCase } from "../StandaloneDatabase/StandaloneDatabaseUtils";
import LogService from "../LogService";
import {
  CandidateProfile,
  TabsResponse,
  JobDetailsResponse,
  CMPCandidatesResponse,
  CMPFiltersResponse
} from "../../components/ResponseManagerV3/types";
import { getUnlockType } from "../../utils/unlockUtils";

const BaseUrl = process.env.BASE_URL;

const ResponseManagerV3Service = {
  // Get job details by job_id
  async getJobDetails(jobId: string | number): Promise<JobDetailsResponse> {
    // Return mock data (backend not ready)

    try {
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/job/get-job-data",
        body: {
          job_id: jobId
        },
        loaderConfig: { hasLoader: false }
      });

      if (response && (response as any).data) {
        return (response as any).data as JobDetailsResponse;
      }

      throw new Error("Invalid response structure");
    } catch (error) {
      console.error("Error fetching job details:", error);
      throw error;
    }
  },

  // Get tabs for a specific job
  async getTabs(jobId: string | number): Promise<TabsResponse> {
    try {
      const response = await FetchService.postCall({
        url: BaseUrl + `/recruiter/cmp/v1/tabs`,
        body: {
          job_id: jobId
        },
        loaderConfig: { hasLoader: false }
      });

      if (response && (response as any).data) {
        return (response as any).data as TabsResponse;
      }

      throw new Error("Invalid response structure");
    } catch (error) {
      console.error("Error fetching tabs:", error);
      throw error;
    }
  },

  // Get filters for CMP v1 (job-based)
  async getCMPFilters(
    jobId: string | number,
    tabId?: string,
    filters?: any[]
  ): Promise<CMPFiltersResponse> {
    try {
      let url = `${BaseUrl}/recruiter/cmp/v1/filters`;

      const filtersArray = filters || [];
      const response = await FetchService.postCall({
        url,
        body: {
          job_id: jobId,
          tab_id: tabId,
          filters: filtersArray,
          DEFAULT_FILTERS: filtersArray.length === 0
        },
        loaderConfig: { hasLoader: false }
      });

      const responseData = (response as any)?.data?.data;
      const filtersData = responseData?.filters;

      return {
        sections: filtersData
      };
    } catch (error) {
      console.error("Error fetching CMP filters:", error);
      throw error;
    }
  },

  // Get candidates for CMP v1 (job-based)
  async getCMPCandidates(
    jobId: string | number,
    tabId: string,
    requestBody?: any
  ): Promise<CMPCandidatesResponse> {
    try {
      const response = await FetchService.postCall({
        url: `${BaseUrl}/recruiter/cmp/v1/candidates`,
        body: {
          job_id: jobId,
          tab_id: tabId,
          page: requestBody.page,
          size: requestBody.size,
          filters: requestBody.filters
        },
        loaderConfig: { hasLoader: false }
      });

      if (response && (response as any).data) {
        const responseData = (response as any).data as CMPCandidatesResponse;

        // Normalize name fields to title case
        if (responseData.data?.candidates) {
          responseData.data.candidates = responseData.data.candidates.map(
            (candidate: CandidateProfile): CandidateProfile => ({
              ...candidate,
              full_name: toTitleCase(candidate.full_name)
            })
          );
        }

        return responseData;
      }

      throw new Error("Invalid response structure");
    } catch (error) {
      console.error("Error fetching CMP candidates:", error);
      throw error;
    }
  },

  // Export candidates
  async exportCandidates(candidateIds: number[], jobId: number): Promise<void> {
    try {
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/job/download-candidates",
        body: {
          job_id: jobId,
          user_ids: candidateIds,
          source: "CMP"
        },
        loaderConfig: { hasLoader: true },
        responseType: "blob"
      });

      const fileName = getFileName(jobId);

      // Use generic download function
      if (response) {
        downloadBlobAsFile(response, fileName);
      }
    } catch (error) {
      console.error("Error exporting candidates:", error);
      throw error;
    }
  },

  // Unlock candidate(s) - supports single or multiple candidates
  async unlockCandidate(
    userIds: number | number[],
    jobId: number
  ): Promise<{ status: boolean; message?: string }> {
    try {
      // Ensure userIds is always an array
      const candidateIds = Array.isArray(userIds) ? userIds : [userIds];

      // Determine type based on number of candidates
      const type = getUnlockType(candidateIds);

      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/job/unlock-candidate",
        body: {
          job_id: jobId,
          user_ids: candidateIds,
          source: "CMP",
          type
        },
        loaderConfig: { hasLoader: true }
      });

      // Handle the response structure
      if (response && (response as any).data) {
        return (response as any).data as { status: boolean; message?: string };
      }

      // Fallback response
      return {
        status: true,
        message: "Candidate(s) unlocked successfully"
      };
    } catch (error) {
      console.error("Error unlocking candidate(s):", error);
      throw error;
    }
  },

  // Change user status (for Call, WhatsApp, Mark as hired, Remove)
  async changeUserStatus({
    jobId,
    userId,
    status,
    reasonText,
    subStatus,
    candidateType,
    optionIds
  }: {
    jobId: number;
    userId: number;
    status:
    | "CALLED"
    | "MESSAGED"
    | "HIRED"
    | "REJECTED"
    | "INVITED"
    | "CALLBACK_REQUESTED";
    reasonText?: string;
    subStatus?: string;
    candidateType?: "applicant" | "recommended";
    optionIds?: number[];
  }): Promise<any> {
    try {
      if (!jobId || !userId || !status) {
        throw new Error("Error changing user status");
      }
      const body = {
        job_id: jobId,
        user_id: userId,
        status: status,
        reason_text: reasonText,
        sub_status: subStatus,
        candidate_type: candidateType,
        option_ids: optionIds
      };
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/cmp/v1/change-candidate-status",
        body,
        loaderConfig: { hasLoader: false }
      });
      if (response && (response as any).data) {
        return (response as any).data as { status: boolean; message?: string };
      }
      return {
        status: true,
        message: "Status updated successfully"
      };
    } catch (error) {
      throw error;
    }
  },

  // Get rejection/removal reasons options
  async getRejectionReasons(): Promise<{
    status: boolean;
    reasons: Array<{ label: string; option_id: number }>;
  }> {
    try {
      const response = await FetchService.getCall({
        url: BaseUrl + "/v2/metadata/recruiter_rejection_feedback_options",
        query: {},
        loaderConfig: { hasLoader: false }
      });

      if (response && (response as any).data) {
        const data = (response as any).data.data;
        const options =
          data?.recruiter_rejection_feedback_options?.options || [];

        return {
          status: true,
          reasons: options.map(
            (option: { label: string; option_id: number }) => ({
              label: option.label,
              option_id: option.option_id
            })
          )
        };
      }

      return {
        status: false,
        reasons: []
      };
    } catch (error) {
      console.error("Error fetching rejection reasons:", error);
      throw error;
    }
  },

  // Undo candidate status change (Hired, Rejected, Removed)
  async undoCandidateStatus(
    jobId: number,
    userId: number,
    status: "HIRED" | "REJECTED"
  ): Promise<{ status: boolean; message?: string; resultantTab?: string }> {
    try {
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/job/undo-candidate-status",
        body: {
          jobId,
          userId,
          status
        },
        loaderConfig: { hasLoader: false }
      });

      if (response && (response as any).data) {
        const responseData = (response as any).data;
        return {
          status: responseData.status ?? true,
          message: responseData.message,
          resultantTab: responseData.data?.resultantTab
        };
      }

      return {
        status: true,
        message: "Status undone successfully"
      };
    } catch (error) {
      console.error("Error undoing candidate status:", error);
      throw error;
    }
  },

  // Log candidate impression
  async logCandidateImpression(
    jobId: string | number,
    userId: number,
    type: "applied" | "invited"
  ): Promise<void> {
    try {
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/job/log-impressions",
        body: {
          job_id: jobId,
          type,
          users: [userId]
        },
        loaderConfig: { hasLoader: false }
      });
    } catch (error) {
      console.error("Error logging candidate impression:", error);
    }
  },

  async changeCandidateStatusDropdown({ userId, jobId, candidate_status }) {
    // POST /recruiter/cmp/v1/update-candidate-status
    try {
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/cmp/v1/update-candidate-status",
        body: {
          user_id: userId,
          job_id: jobId,
          candidate_status
        },
        loaderConfig: { hasLoader: false }
      });
      if (response && (response as any).data) {
        return (response as any).data as { status: boolean; message?: string };
      }
      return {
        status: true,
        message: "Candidate status updated successfully"
      };
    } catch (error) {
      throw error;
    }
  },

  // Get candidate screening questions and answers
  async getCandidateScreeningQuestions(
    jobId: string | number,
    userId: number
  ): Promise<{
    status: boolean;
    data: {
      questions: Array<{
        question_id: number;
        question_label: string;
        meta_label: string;
        options: Array<{
          option_id: number;
          option_label: string;
          is_selected: number;
        }>;
      }>;
    }
  }> {
    try {
      const response = await FetchService.postCall({
        url: BaseUrl + "/recruiter/job/candidate-screening-questions",
        body: {
          jobId,
          userId
        },
        loaderConfig: { hasLoader: false }
      });

      if (response && (response as any)?.data && (response as any)?.data?.data) {
        return {
          status: true,
          data: (response as any)?.data?.data
        };
      }

      // Return empty response when no data is available
      return {
        status: false,
        data: { questions: [] }
      };
    } catch (error) {
      console.error("Error fetching candidate screening questions:", error);
      throw error;
    }
  },
  async getEligibleJobsForBulkWA(): Promise<{
    status: boolean;
    jobs: Array<{
      job_id: number;
      job_title: string;
      locality?: string;
      city?: string;
      posted_on?: string;
      salary?: string;
    }>;
  }> {
    try {
      interface EligibleJobsResponse {
        data?: {
          jobs?: Array<{
            job_id: number;
            job_title: string;
            locality?: string;
            city?: string;
            posted_on?: string;
            salary?: string;
          }>;
          data?: {
            jobs?: Array<{
              job_id: number;
              job_title: string;
              locality?: string;
              city?: string;
              posted_on?: string;
              salary?: string;
            }>;
          };
        };
      }

      const response = (await FetchService.getCall({
        url: BaseUrl + "/recruiter/job/eligible-jobs",
        query: {
          type: "BULK_WA"
        },
        loaderConfig: { hasLoader: false }
      })) as EligibleJobsResponse;

      if (response && response.data) {
        const data = response.data;
        return {
          status: true,
          jobs: data?.jobs || data?.data?.jobs || []
        };
      }

      return {
        status: false,
        jobs: []
      };
    } catch (error) {
      // Error is handled by FetchService interceptors, but we log for debugging
      console.error("Error fetching eligible jobs for bulk WhatsApp:", error);
      throw error;
    }
  }
};

export default ResponseManagerV3Service;
